/**
 * Clients — Contact list page
 * Columns: Organization, President/Manager, Phone, Email, State, Industry
 * Features: search, CSV import, manual create, inline edit, delete, export,
 *           quick SMS / Email compose from row hover actions
 * Styled for the light (#F8F6F2) dashboard background.
 * State defaults to CA for all new contacts.
 */

import { useState, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { SmsComposeModal } from "@/components/SmsComposeModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Upload,
  Plus,
  Download,
  Pencil,
  Trash2,
  X,
  Check,
  User,
  MessageSquare,
  Mail,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Client {
  id: number;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  clientState: string | null;
  industry: string | null;
  contactPerson: string | null;
  notes: string | null;
  clientType: string;
  isCoreClient: boolean;
}

interface CreateForm {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientState: string;
  industry: string;
  contactPerson: string;
}

const EMPTY_FORM: CreateForm = {
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  clientState: "CA",
  industry: "",
  contactPerson: "",
};

// ─── CSV parser ───────────────────────────────────────────────────────────────
function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map((line) => {
    const values: string[] = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; continue; }
      if (ch === "," && !inQuote) { values.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    values.push(cur.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ""; });
    return row;
  });
}

function mapCsvRow(row: Record<string, string>) {
  const get = (...keys: string[]) => {
    for (const k of keys) {
      const v = row[k] ?? row[k.replace(/ /g, "_")] ?? row[k.replace(/_/g, " ")];
      if (v) return v;
    }
    return "";
  };
  return {
    clientName: get("name", "client_name", "clientname", "full_name", "contact_name", "company", "company_name", "organization"),
    clientEmail: get("email", "email_address", "clientemail"),
    clientPhone: get("phone", "phone_number", "mobile", "cell", "clientphone"),
    clientState: get("state", "state_province", "province", "region", "clientstate") || "CA",
    industry: get("industry", "sector", "category", "type"),
    contactPerson: get("contact_person", "contactperson", "president", "manager", "owner", "contact", "primary_contact"),
  };
}

// ─── Phone formatting helpers ───────────────────────────────────────────────

/**
 * Format a single US phone number string into (XXX) XXX-XXXX.
 * Handles 10-digit and 11-digit (1+10) numbers.
 * Returns the raw input unchanged if it doesn't look like a US number.
 */
function formatSinglePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Partial entry — format as far as we can
  if (digits.length <= 3) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return raw; // too long / international — leave as-is
}

/**
 * Format a full phone field value (may contain multiple numbers separated by ; or ,).
 * Each segment is formatted independently.
 */
function formatPhoneField(raw: string): string {
  return raw
    .split(/[;,]/)
    .map((seg) => {
      const trimmed = seg.trim();
      return trimmed ? formatSinglePhone(trimmed) : "";
    })
    .filter(Boolean)
    .join(" ; ");
}

/** Digit count for a single phone segment */
function digitCount(seg: string): number {
  return seg.replace(/\D/g, "").length;
}

/** Validation status for a single phone segment */
function phoneSegmentStatus(seg: string): "valid" | "warn" | "empty" {
  const d = digitCount(seg);
  if (d === 0) return "empty";
  if (d === 10 || d === 11) return "valid";
  return "warn";
}

/**
 * PhoneInput — smart phone field that:
 * - Auto-formats each segment as (XXX) XXX-XXXX while typing
 * - Shows a per-segment digit counter badge (green = 10/11 digits, amber = wrong count)
 * - Accepts multiple numbers separated by " ; "
 */
function PhoneInput({
  value,
  onChange,
  className,
  compact = false,
}: {
  value: string;
  onChange: (val: string) => void;
  className?: string;
  compact?: boolean;
}) {
  // Raw display value (what the user sees in the input)
  const [display, setDisplay] = useState(value);

  // Sync external value changes (e.g. when edit row is opened)
  const prevValueRef = useRef(value);
  if (prevValueRef.current !== value) {
    prevValueRef.current = value;
    setDisplay(value);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDisplay(raw);
    onChange(raw);
  };

  const handleBlur = () => {
    // On blur, format all segments
    const formatted = formatPhoneField(display);
    if (formatted !== display) {
      setDisplay(formatted);
      onChange(formatted);
    }
  };

  // Parse segments for the digit counter
  const segments = display
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const hasWarning = segments.some((s) => phoneSegmentStatus(s) === "warn");

  return (
    <div>
      <input
        type="tel"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="(310) 847-0245 ; (562) 488-3380"
        title="Separate multiple numbers with semicolons. Auto-formats on blur."
        className={[
          "flex w-full rounded-md border px-3 text-sm ring-offset-background",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "bg-white border-stone-300 text-stone-800 placeholder:text-stone-400",
          compact ? "h-7" : "h-9",
          hasWarning ? "border-amber-400 focus-visible:ring-amber-400" : "",
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
      />
      {/* Per-segment digit counter badges */}
      {segments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {segments.map((seg, i) => {
            const status = phoneSegmentStatus(seg);
            const d = digitCount(seg);
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                style={{
                  background:
                    status === "valid"
                      ? "oklch(0.92 0.10 142)"
                      : status === "warn"
                      ? "oklch(0.95 0.12 75)"
                      : "oklch(0.92 0.01 240)",
                  color:
                    status === "valid"
                      ? "oklch(0.28 0.14 142)"
                      : status === "warn"
                      ? "oklch(0.35 0.14 75)"
                      : "oklch(0.40 0.04 240)",
                  border: `1px solid ${
                    status === "valid"
                      ? "oklch(0.72 0.18 142)"
                      : status === "warn"
                      ? "oklch(0.72 0.16 75)"
                      : "oklch(0.78 0.02 240)"
                  }`,
                }}
              >
                {status === "valid" ? "✓" : "⚠"} {d} digit{d !== 1 ? "s" : ""}
              </span>
            );
          })}
          {hasWarning && (
            <span className="text-xs text-amber-600 font-medium self-center">
              US numbers need 10 digits
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline email compose modal ───────────────────────────────────────────────
function EmailComposeModal({
  open,
  onClose,
  initialTo,
  initialName,
  staffName,
  staffEmail,
  staffRole,
}: {
  open: boolean;
  onClose: () => void;
  initialTo: string;
  initialName: string;
  staffName: string;
  staffEmail: string;
  staffRole: string;
}) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const composeMutation = trpc.email.compose.useMutation({
    onSuccess: () => {
      toast.success("Email sent successfully");
      handleClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleClose = () => {
    setTo(initialTo);
    setSubject("");
    setBody("");
    onClose();
  };

  const handleSend = () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Please fill in To, Subject, and Body");
      return;
    }
    composeMutation.mutate({
      toEmail: to.trim(),
      toName: initialName || to.trim(),
      subject: subject.trim(),
      body: body.trim(),
      clientName: initialName || undefined,
      staffName,
      staffEmail,
      staffRole,
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "oklch(0 0 0 / 70%)" }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: "oklch(0.97 0.005 85)",
          border: "1px solid oklch(0 0 0 / 12%)",
          maxHeight: "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid oklch(0 0 0 / 12%)" }}
        >
          <h2
            className="text-base font-bold"
            style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}
          >
            New Email
          </h2>
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5"
            style={{ color: "oklch(0.14 0.04 240)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>TO</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@email.com"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: "oklch(0.94 0.018 220)",
                border: "1px solid oklch(0 0 0 / 12%)",
                color: "oklch(0.12 0.04 240)",
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>SUBJECT</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: "oklch(0.94 0.018 220)",
                border: "1px solid oklch(0 0 0 / 12%)",
                color: "oklch(0.12 0.04 240)",
              }}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>MESSAGE</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message…"
              rows={8}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
              style={{
                background: "oklch(0.94 0.018 220)",
                border: "1px solid oklch(0 0 0 / 12%)",
                color: "oklch(0.12 0.04 240)",
              }}
            />
          </div>
        </div>

        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderTop: "1px solid oklch(0 0 0 / 12%)" }}
        >
          <span className="text-sm" style={{ color: "oklch(0.35 0.02 240)" }}>
            From: apxadvisor@gmail.com
          </span>
          <button
            onClick={handleSend}
            disabled={composeMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, oklch(0.72 0.14 75), oklch(0.6 0.12 75))",
              color: "oklch(0.12 0.04 240)",
            }}
          >
            {composeMutation.isPending ? "Sending…" : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline edit row ──────────────────────────────────────────────────────────
function EditableRow({
  client,
  onSave,
  onCancel,
}: {
  client: Client;
  onSave: (patch: Partial<Client>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(client.clientName);
  const [contactPerson, setContactPerson] = useState(client.contactPerson ?? "");
  const [email, setEmail] = useState(client.clientEmail ?? "");
  const [phone, setPhone] = useState(client.clientPhone ?? "");
  const [state, setState] = useState(client.clientState ?? "CA");
  const [industry, setIndustry] = useState(client.industry ?? "");
  const [notes, setNotes] = useState(client.notes ?? "");

  return (
    <>
    <tr className="bg-amber-50 border-b border-stone-200">
      <td className="px-4 py-2" />
      <td className="px-4 py-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-7 text-sm bg-amber-50 border-amber-300 text-stone-400 placeholder:text-stone-300"
          placeholder="Organization name"
        />
      </td>
      <td className="px-4 py-2">
        <Input
          value={contactPerson}
          onChange={(e) => setContactPerson(e.target.value)}
          className="h-7 text-sm bg-amber-50 border-amber-300 text-stone-400 placeholder:text-stone-300"
          placeholder="President / Manager"
        />
      </td>
      <td className="px-4 py-2">
        <PhoneInput
          value={phone}
          onChange={(val) => setPhone(val)}
          compact
        />
      </td>
      <td className="px-4 py-2">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
          className="h-7 text-sm bg-amber-50 border-amber-300 text-stone-400 placeholder:text-stone-300"
        />
      </td>
      <td className="px-4 py-2">
        <Input
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="h-7 text-sm bg-amber-50 border-amber-300 text-stone-400 placeholder:text-stone-300 w-16"
          placeholder="CA"
        />
      </td>
      <td className="px-4 py-2">
        <Input
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          className="h-7 text-sm bg-amber-50 border-amber-300 text-stone-400 placeholder:text-stone-300"
          placeholder="Restaurant, Auto…"
        />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() =>
              onSave({
                clientName: name.trim() || client.clientName,
                contactPerson: contactPerson.trim() || null,
                clientEmail: email.trim() || null,
                clientPhone: phone.trim() || null,
                clientState: state.trim() || "CA",
                industry: industry.trim() || null,
                notes: notes.trim() || null,
              })
            }
            className="p-1 rounded text-green-600 hover:bg-green-100"
          >
            <Check className="w-4 h-4" />
          </button>
          <button onClick={onCancel} className="p-1 rounded text-red-500 hover:bg-red-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
    {/* Notes row — spans full width below the inline edit fields */}
    <tr className="bg-amber-50">
      <td colSpan={8} className="px-4 pb-3">
        <div className="flex items-start gap-2">
          <label className="text-xs font-semibold w-20 shrink-0 pt-2" style={{ color: "#6b5e4e" }}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes about this client…"
            rows={2}
            className="flex-1 px-2 py-1.5 rounded-md text-sm outline-none resize-y border border-amber-300 bg-amber-50 text-stone-400 placeholder:text-stone-300 focus:border-amber-500"
            style={{ minHeight: "52px", fontFamily: "inherit" }}
          />
        </div>
      </td>
    </tr>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Clients() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [tosAgreed, setTosAgreed] = useState(false);
  const [importPreview, setImportPreview] = useState<ReturnType<typeof mapCsvRow>[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter: "all" | "monthly" | "annual" | "non-reporting"
  const [filter, setFilter] = useState<"all" | "monthly" | "annual" | "non-reporting">("all");

  // Quick-compose state
  const [smsTarget, setSmsTarget] = useState<{ phone: string; name: string } | null>(null);
  const [emailTarget, setEmailTarget] = useState<{ email: string; name: string } | null>(null);
  // Multi-number picker: holds the client.id whose number picker dropdown is open
  const [smsPickerId, setSmsPickerId] = useState<number | null>(null);
  const utils = trpc.useUtils();

  const { data: allClients = [], isLoading } = trpc.communications.listAll.useQuery({ search });

  // Apply filter on top of search results
  const clients = allClients.filter((c: any) => {
    if (filter === "monthly") return c.isCoreClient && !c.isAnnual;
    if (filter === "annual") return c.isCoreClient && c.isAnnual;
    if (filter === "non-reporting") return !c.isCoreClient;
    return true; // "all"
  });

  // Row selection state — must be after `clients` is declared
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const toggleSelect = (id: number) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = clients.length > 0 && clients.every((c: any) => selectedIds.has(c.id));
  const someSelected = clients.some((c: any) => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(clients.map((c: any) => c.id)));
    }
  };

  // Counts for the header breakdown (always from full unfiltered list)
  const monthlyCount = allClients.filter((c: any) => c.isCoreClient && !c.isAnnual).length;
  const annualCount = allClients.filter((c: any) => c.isCoreClient && c.isAnnual).length;
  const nonReportingCount = allClients.filter((c: any) => !c.isCoreClient).length;

  const createMut = trpc.communications.createContact.useMutation({
    onSuccess: () => {
      utils.communications.listAll.invalidate();
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setTosAgreed(false);
      toast.success("Contact created");
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMut = trpc.communications.updateContact.useMutation({
    onSuccess: () => {
      utils.communications.listAll.invalidate();
      setEditingId(null);
      toast.success("Contact updated");
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMut = trpc.communications.deleteContact.useMutation({
    onSuccess: () => {
      utils.communications.listAll.invalidate();
      toast.success("Contact deleted");
    },
    onError: (e) => toast.error(e.message),
  });

  const importMut = trpc.communications.importContacts.useMutation({
    onSuccess: (res) => {
      utils.communications.listAll.invalidate();
      setShowImport(false);
      setImportPreview([]);
      toast.success(`Import complete — ${res.inserted} added, ${res.updated} updated`);
    },
    onError: (e) => toast.error(e.message),
  });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCsv(text);
      const mapped = rows.map(mapCsvRow).filter((r) => r.clientName.trim());
      setImportPreview(mapped);
      if (mapped.length === 0) toast.error("No valid rows found. Check that your CSV has a 'name' column.");
    };
    reader.readAsText(file);
    e.target.value = "";
  }, []);

  const handleExport = useCallback(() => {
    const header = "Organization,President/Manager,Phone,Email,State,Industry,Type";
    const rows = clients.map((c) =>
      [
        c.clientName,
        (c as unknown as Client).contactPerson ?? "",
        c.clientPhone ?? "",
        c.clientEmail ?? "",
        c.clientState ?? "CA",
        c.industry ?? "",
        (c as any).isAnnual ? "Annual" : c.isCoreClient ? "Monthly" : "Non-Reporting",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const suffix = filter === "all" ? "all" : filter;
    a.download = `apx-contacts-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [clients, filter]);

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    deleteMut.mutate({ id });
  };

  const avatar = (name: string) =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  const staffName = user?.name ?? "APX Team";
  const staffEmail = user?.email ?? "apxadvisor@gmail.com";
  const staffRole = user?.role ?? "staff";

  // ── Contact editing permissions ──────────────────────────────────────────────
  // Edit (pencil): super_admin, admin, controller, asst_controller, bookkeeping_manager
  const canEditContact = ["super_admin", "admin", "controller", "asst_controller", "bookkeeping_manager"].includes(staffRole);
  // Delete: super_admin, admin, controller only
  const canDeleteContact = ["super_admin", "admin", "controller"].includes(staffRole);
  // Import / Create: super_admin, admin, controller only
  const canImportContact = ["super_admin", "admin", "controller"].includes(staffRole);

  return (
    <DashboardLayout noPadding>
      {/* ── Page wrapper: warm taupe background ── */}
      <div
        className="flex flex-col h-full min-h-0 overflow-auto"
        style={{ background: "#D9D3C7" }}
      >
        {/* ── Page Header (outside the white card, matching reference) ── */}
        <div className="shrink-0 px-8 pt-8 pb-4">
          <div className="flex items-start justify-between gap-4">
            {/* Title + stats */}
            <div>
              <h1
                className="font-bold leading-none tracking-tight"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "2.25rem",
                  color: "#1a1a1a",
                }}
              >
                Contacts
              </h1>
              <p className="mt-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1" style={{ color: "#5c5346" }}>
                <span className="font-medium" style={{ color: "#1a1a1a" }}>{allClients.length} total</span>
                <span style={{ color: "#b0a898" }}>·</span>
                <span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: "#e8f5e9", color: "#2e7d32" }}
                  >
                    {monthlyCount} monthly
                  </span>
                </span>
                <span style={{ color: "#b0a898" }}>·</span>
                <span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: "#e3f2fd", color: "#1565c0" }}
                  >
                    {annualCount} annual
                  </span>
                </span>
                <span style={{ color: "#b0a898" }}>·</span>
                <span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: "#f5f5f5", color: "#616161" }}
                  >
                    {nonReportingCount} non-reporting
                  </span>
                </span>
              </p>
            </div>
            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-1 shrink-0">
              {canImportContact && (
                <button
                  onClick={() => { setShowImport(true); setImportPreview([]); }}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    border: "1px solid rgba(0,0,0,0.15)",
                    color: "#3d3328",
                  }}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Import
                </button>
              )}
              {canImportContact && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all hover:brightness-110"
                  style={{
                    background: "linear-gradient(135deg, #C9A84C, #B8922A)",
                    color: "#fff",
                    boxShadow: "0 2px 8px rgba(184,146,42,0.35)",
                  }}
                >
                  <Plus className="w-4 h-4" />
                  + Create
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Main White Card ── */}
        <div
          className="flex-1 min-h-0 flex flex-col mx-8 mb-8 overflow-hidden"
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          {/* ── Toolbar ── */}
          <div className="shrink-0 flex items-center gap-3 px-6 py-4 flex-wrap" style={{ borderBottom: "1px solid #f0ece6" }}>
            {/* Search */}
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#a09080" }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{
                  background: "#f7f4f0",
                  border: "1px solid #e0d9d0",
                  borderRadius: "10px",
                  color: "#1a1a1a",
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#a09080" }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {/* Segmented filter pills */}
            <div
              className="flex items-center shrink-0 overflow-hidden"
              style={{
                border: "1px solid #d4ccc0",
                borderRadius: "10px",
                background: "#f7f4f0",
              }}
            >
              {([
                { key: "all",           label: "All" },
                { key: "monthly",       label: "Monthly" },
                { key: "annual",        label: "Annual" },
                { key: "non-reporting", label: "Non-Reporting" },
              ] as const).map(({ key, label }, i, arr) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className="px-4 py-1.5 text-xs font-semibold transition-all"
                  style={{
                    borderRight: i < arr.length - 1 ? "1px solid #d4ccc0" : "none",
                    background: filter === key ? "#B8922A" : "transparent",
                    color: filter === key ? "#fff" : "#6b5e4e",
                    borderRadius: 0,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Table ── */}
          <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10" style={{ background: "#EDE8E0", borderBottom: "1px solid #D4CCC0" }}>
              <tr>
                <th className="px-4 w-8 text-center" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={toggleSelectAll}
                    style={{ accentColor: "#B8922A", cursor: "pointer" }}
                  />
                </th>
                <th className="text-center px-4" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap", minWidth: "360px", width: "360px" }}>Organization</th>
                <th className="text-center px-4" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap" }}>Contact</th>
                <th className="text-center px-4" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap" }}>Phone</th>
                <th className="text-center px-4" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap" }}>Email</th>
                <th className="text-center px-4 w-16" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap" }}>State</th>
                <th className="text-center px-4" style={{ paddingTop: "10px", paddingBottom: "10px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7a6a58", whiteSpace: "nowrap", width: "100px", maxWidth: "100px" }}>Industry</th>
                <th className="w-28" />
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-stone-400">
                    Loading contacts…
                  </td>
                </tr>
              )}
              {!isLoading && clients.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-stone-400">
                      <User className="w-10 h-10 opacity-30" />
                      <p className="text-sm">
                        {search ? "No contacts match your search." : filter !== "all" ? `No ${filter} contacts found.` : "No contacts yet. Import a CSV or create one."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {clients.map((client) =>
                editingId === client.id ? (
                  <EditableRow
                    key={client.id}
                    client={client as unknown as Client}
                    onSave={(patch) => updateMut.mutate({ id: client.id, ...patch })}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <tr
                    key={client.id}
                    className="group transition-colors"
                    style={{ borderBottom: "1px solid #f0ece6" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#faf8f5")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                  >
                    <td className="px-4 py-3.5">
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer"
                        checked={selectedIds.has(client.id)}
                        onChange={() => toggleSelect(client.id)}
                        style={{ accentColor: "#B8922A", cursor: "pointer" }}
                      />
                    </td>
                    {/* Organization */}
                    <td className="px-4 py-3.5" style={{ minWidth: "360px", width: "360px" }}>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                          style={{ background: "#B8922A", color: "#fff" }}
                        >
                          {avatar(client.clientName)}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="font-bold"
                              style={{
                                fontSize: "0.95rem",
                                fontFamily: "'DM Sans', sans-serif",
                                color: !client.isCoreClient ? "#a09080" : "#1a1a1a",
                                fontStyle: !client.isCoreClient ? "italic" : "normal",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {client.clientName}
                            </span>
                            {(client as any).isAnnual && client.isCoreClient && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0"
                                style={{ background: "#e3f2fd", color: "#1565c0" }}
                              >
                                Annual
                              </span>
                            )}
                          </div>
                          {(client as unknown as Client).notes && (
                            <span
                              className="text-[11px] truncate"
                              style={{ color: "#9a7a2a", fontStyle: "italic", maxWidth: "300px" }}
                              title={(client as unknown as Client).notes ?? ""}
                            >
                              {(client as unknown as Client).notes}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* President / Manager */}
                    <td className="px-4 py-3.5 text-sm" style={{ color: "#6b5e4e" }}>
                      {(client as unknown as Client).contactPerson ?? <span style={{ color: "#c8bfb5" }}>—</span>}
                    </td>
                    {/* Phone — renders each semicolon-separated number as a chip */}
                    <td className="px-4 py-3.5 text-center" style={{ color: "#3d3328" }}>
                      {client.clientPhone ? (
                        <div className="flex flex-wrap gap-1">
                          {client.clientPhone.split(/[;,]/).map((n) => n.trim()).filter(Boolean).map((num, idx) => (
                            <span
                              key={idx}
                              className="inline-block whitespace-nowrap"
                              style={{ color: "#3d3328", fontFamily: "'DM Mono', 'Courier New', monospace", fontSize: "0.875rem", fontWeight: 500 }}
                            >
                              {num}
                            </span>
                          ))}
                        </div>
                      ) : <span style={{ color: "#c8bfb5" }}>—</span>}
                    </td>
                    {/* Email */}
                    <td className="px-4 py-3.5 text-center">
                      {client.clientEmail
                        ? <span style={{ color: "#3d3328", fontFamily: "'DM Mono', 'Courier New', monospace", fontSize: "0.875rem", fontWeight: 500, display: "inline-block", whiteSpace: "nowrap" }}>{client.clientEmail}</span>
                        : <span style={{ color: "#c8bfb5" }}>—</span>}
                    </td>
                    {/* State */}
                    <td className="px-4 py-3.5 text-sm font-semibold" style={{ color: "#3d3328" }}>
                      {client.clientState ?? <span style={{ color: "#a09080" }}>CA</span>}
                    </td>
                    {/* Industry */}
                    <td className="px-4 py-3.5 text-sm" style={{ color: "#6b5e4e", width: "100px", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {client.industry ?? <span style={{ color: "#c8bfb5" }}>—</span>}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1 transition-opacity">
                        {/* SMS button: single number → open compose; multiple → show picker */}
                        <div className="relative">
                          <button
                            onClick={() => {
                              if (!client.clientPhone) {
                                toast.error("No phone number on file for this contact");
                                return;
                              }
                              const nums = client.clientPhone.split(/[;,]/).map((n) => n.trim()).filter(Boolean);
                              if (nums.length === 1) {
                                setSmsTarget({ phone: nums[0], name: client.clientName });
                              } else {
                                setSmsPickerId((prev) => prev === client.id ? null : client.id);
                              }
                            }}
                            className="p-1.5 rounded text-stone-400 hover:text-green-600 hover:bg-green-50"
                            title="Send SMS"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                          {smsPickerId === client.id && client.clientPhone && (
                            <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[180px]">
                              <p className="px-3 py-1 text-xs text-stone-400 font-medium uppercase tracking-wide">Select number</p>
                              {client.clientPhone.split(/[;,]/).map((n) => n.trim()).filter(Boolean).map((num, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => { setSmsPickerId(null); setSmsTarget({ phone: num, name: client.clientName }); }}
                                  className="w-full text-left px-3 py-1.5 text-xs font-mono text-stone-700 hover:bg-amber-50 hover:text-amber-800 transition-colors"
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            if (!client.clientEmail) {
                              toast.error("No email address on file for this contact");
                              return;
                            }
                            setEmailTarget({ email: client.clientEmail, name: client.clientName });
                          }}
                          className="p-1.5 rounded text-stone-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Send Email"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </button>
                        {canEditContact && (
                          <button
                            onClick={() => setEditingId(client.id)}
                            className="p-1.5 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-100"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDeleteContact && (
                          <button
                            onClick={() => handleDelete(client.id, client.clientName)}
                            className="p-1.5 rounded text-stone-400 hover:text-red-500 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          </div>{/* end table scroll */}

          {/* ── Footer ── */}
          <div
            className="shrink-0 px-6 py-2.5 text-right"
            style={{ borderTop: "1px solid #f0ece6" }}
          >
            <span className="text-xs font-medium" style={{ color: "#a09080" }}>
              {clients.length} contact{clients.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>{/* end main white card */}
      </div>

      {/* ── Create Modal ── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs text-stone-500 mb-1">Organization *</label>
              <Input
                value={form.clientName}
                onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))}
                placeholder="Smith Auto Repair Inc."
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">President / Manager</label>
              <Input
                value={form.contactPerson}
                onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Phone (separate multiple with ;)</label>
              <PhoneInput
                value={form.clientPhone}
                onChange={(val) => setForm((f) => ({ ...f, clientPhone: val }))}
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Email</label>
              <Input
                value={form.clientEmail}
                onChange={(e) => setForm((f) => ({ ...f, clientEmail: e.target.value }))}
                placeholder="john@smithauto.com"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">State / Province</label>
              <Input
                value={form.clientState}
                onChange={(e) => setForm((f) => ({ ...f, clientState: e.target.value }))}
                placeholder="CA"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Industry / Type</label>
              <Input
                value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                placeholder="Auto Repair, Restaurant…"
              />
            </div>
          </div>
          <div className="px-1 pb-1">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tosAgreed}
                onChange={(e) => setTosAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[oklch(0.72_0.14_75)]"
              />
              <span className="text-xs text-stone-600 leading-snug">
                I confirm this client has agreed to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                  className="underline text-amber-700 hover:text-amber-900">
                  Terms of Service
                </a>{" "}and{" "}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                  className="underline text-amber-700 hover:text-amber-900">
                  Privacy Policy
                </a>.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowCreate(false); setTosAgreed(false); }}>
              Cancel
            </Button>
            <Button
              className="bg-[oklch(0.72_0.14_75)] hover:bg-[oklch(0.65_0.14_75)] text-[oklch(0.15_0.04_240)] font-semibold"
              disabled={!form.clientName.trim() || !tosAgreed || createMut.isPending}
              onClick={() => createMut.mutate(form)}
            >
              {createMut.isPending ? "Creating…" : "Create Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Import Modal ── */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-stone-500">
              Upload a CSV with columns:{" "}
              <span className="text-stone-700 font-mono">name, contact_person, phone, email, state, industry</span>{" "}
              (order doesn't matter). State defaults to <strong>CA</strong> if omitted.
            </p>
            <div
              className="border-2 border-dashed border-stone-200 rounded-lg p-8 text-center cursor-pointer hover:border-[oklch(0.72_0.14_75)] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-stone-400" />
              <p className="text-sm text-stone-500">Click to select a CSV file</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {importPreview.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 mb-2">
                  Preview — {importPreview.length} row{importPreview.length !== 1 ? "s" : ""} detected
                </p>
                <div className="max-h-48 overflow-auto rounded border border-stone-200">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50">
                      <tr>
                        {["Organization", "President/Manager", "Phone", "Email", "State", "Industry"].map((h) => (
                          <th key={h} className="text-left px-3 py-2 text-stone-500 font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          <td className="px-3 py-1.5 text-stone-800">{r.clientName}</td>
                          <td className="px-3 py-1.5 text-stone-500">{r.contactPerson}</td>
                          <td className="px-3 py-1.5 text-stone-500 font-mono">{r.clientPhone}</td>
                          <td className="px-3 py-1.5 text-stone-500">{r.clientEmail}</td>
                          <td className="px-3 py-1.5 text-stone-500">{r.clientState || "CA"}</td>
                          <td className="px-3 py-1.5 text-stone-500">{r.industry}</td>
                        </tr>
                      ))}
                      {importPreview.length > 10 && (
                        <tr className="border-t border-stone-100">
                          <td colSpan={6} className="px-3 py-1.5 text-stone-400 text-center">
                            +{importPreview.length - 10} more rows…
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setShowImport(false); setImportPreview([]); }}
            >
              Cancel
            </Button>
            <Button
              className="bg-[oklch(0.72_0.14_75)] hover:bg-[oklch(0.65_0.14_75)] text-[oklch(0.15_0.04_240)] font-semibold"
              disabled={importPreview.length === 0 || importMut.isPending}
              onClick={() => importMut.mutate({ contacts: importPreview })}
            >
              {importMut.isPending
                ? `Importing ${importPreview.length} rows…`
                : `Import ${importPreview.length} Contacts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── SMS Quick-Compose ── */}
      {smsTarget && (
        <SmsComposeModal
          open={true}
          onClose={() => setSmsTarget(null)}
          senderName={staffName}
          senderEmail={staffEmail}
          initialPhone={smsTarget.phone}
          initialName={smsTarget.name}
          userRole={staffRole}
        />
      )}

      {/* ── Email Quick-Compose ── */}
      {emailTarget && (
        <EmailComposeModal
          open={true}
          onClose={() => setEmailTarget(null)}
          initialTo={emailTarget.email}
          initialName={emailTarget.name}
          staffName={staffName}
          staffEmail={staffEmail}
          staffRole={staffRole}
        />
      )}
    </DashboardLayout>
  );
}
